/*
 * EG Receipt Scanner
 * ------------------
 * Claims the Aura Points of a trade-fair POS purchase. The visitor enters
 * their email and scans the barcode on their printed receipt (it encodes
 * the Shopify numeric order id); we then POST both through the app proxy:
 *
 *   POST {proxyBase}/pointTransactions/pos
 *   { "orderId": "<digits>", "email": "<email>" }
 *
 * The proxy path MUST be relative (single leading slash). An absolute URL
 * would bypass Shopify's app-proxy signing (401) and could post the email
 * to a foreign origin, so anything else is refused (same guard as
 * eg-customer-loyalty.js).
 *
 * Scanning engines, lazy-loaded only when the camera opens:
 *   1. Native BarcodeDetector (Chrome, Edge, Android WebView) — any 1D format.
 *   2. Bundled Code 128 decoder (eg-code128-reader.js) for Safari/iOS/Firefox.
 * Both run against a downscaled canvas frame. A result is only accepted
 * after two consecutive identical reads, and must look like a Shopify
 * numeric order id (6-20 digits). Devices without a usable camera fall
 * back to manual code entry.
 *
 * The live "receipt outline" highlight is a cheap luminance blob tracker:
 * each ~90ms the frame is downsampled to a ~48-cell grid, the largest
 * bright region (the paper) is flood-filled, and its smoothed bounding box
 * is drawn on the overlay canvas. Pure gamification — detection never
 * depends on it.
 */

const ORDER_ID_RE = /^\d{6,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Pull the Shopify numeric order id out of a barcode / manually-typed value.
 * The POS receipt payload is "R:<orderId>" (e.g. "R:13526292169086"), but we
 * stay lenient about prefixes and separators: take the LONGEST digit run —
 * never concatenate separate runs, so "R2:135…" or "#1-1003" can't silently
 * fuse into a different id.
 */
function extractOrderId(raw) {
  const runs = String(raw || '').match(/\d+/g);
  if (!runs) return null;
  const best = runs.reduce((a, b) => (b.length > a.length ? b : a));
  return ORDER_ID_RE.test(best) ? best : null;
}

const DECODE_INTERVAL_MS = 180; // barcode attempt cadence
const OUTLINE_INTERVAL_MS = 90; // paper-outline refresh cadence
const CONFIRM_READS = 2; // identical consecutive reads required
const CAPTURE_LINGER_MS = 1600; // "Order detected" moment before closing
const COUNT_UP_MS = 900;

// Downsampled sizes: decode frame + outline grid. 1280 keeps ~2.5-3.5px per
// barcode module at typical scan distance — the JS fallback decoder needs
// that headroom on blurry camera frames.
const DECODE_MAX_WIDTH = 1280;
const GRID_W = 64;
// The receipt contour is resampled to this many points so consecutive
// frames can be blended point-wise (smooth "liquid" tracking).
const CONTOUR_POINTS = 48;
// One full lap of the border beam along the contour, in ms.
const BEAM_LAP_MS = 2400;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Replace the [[TOKEN]] sentinels baked into the localized strings. */
const fill = (template, tokens) =>
  Object.entries(tokens).reduce(
    (acc, [key, value]) => acc.replaceAll(`[[${key.toUpperCase()}]]`, String(value)),
    template || ''
  );

class EGReceiptScanner extends HTMLElement {
  connectedCallback() {
    if (this.__egInit) return;
    this.__egInit = true;

    const $ = (sel) => this.querySelector(sel);
    this.form = $('[data-eg-rs-form]');
    this.emailInput = $('[data-eg-rs-email]');
    this.codeInput = $('[data-eg-rs-code]');
    this.manualToggle = $('[data-eg-rs-manual-toggle]');
    this.manualWrap = $('[data-eg-rs-manual]');
    this.scanTile = $('.eg-rs__scan-tile');
    this.detectedChip = $('[data-eg-rs-detected]');
    this.detectedText = $('[data-eg-rs-detected-text]');
    this.errorEl = $('[data-eg-rs-error]');
    this.submitBtn = $('[data-eg-rs-submit]');
    this.submitLabel = $('[data-eg-rs-submit-label]');
    this.successPanel = $('[data-eg-rs-success]');
    this.successTitle = $('[data-eg-rs-success-title]');
    this.successBody = $('[data-eg-rs-success-body]');
    this.pointsEl = $('[data-eg-rs-points]');
    this.burstEl = $('[data-eg-rs-burst]');
    this.statusEl = $('[data-eg-rs-status]');

    this.dialog = $('[data-eg-rs-dialog]');
    this.video = $('[data-eg-rs-video]');
    this.overlay = $('[data-eg-rs-overlay]');
    this.captureCanvas = $('[data-eg-rs-capture]');
    this.flashEl = $('[data-eg-rs-flash]');
    this.cameraHint = $('[data-eg-rs-camera-hint]');
    this.banner = $('[data-eg-rs-banner]');
    this.bannerText = $('[data-eg-rs-banner-text]');

    this.orderId = null;
    this.stream = null;
    this.rafId = 0;
    this.captureBeamRafId = 0;
    this.captureBeamTimer = 0;
    this.closeTimer = 0;
    this.detector = null; // { kind: 'native'|'fallback', detect(canvas,ctx) }
    this.lastDecodeAt = 0;
    this.lastOutlineAt = 0;
    this.lastRead = '';
    this.readStreak = 0;
    this.contour = null;
    this.contourLength = 0;
    this.accentColor = '#7c4fd9';
    this.reduceMotion = false;
    this.locked = false;
    this.submitting = false;

    // Reusable offscreen canvases (decode + outline grid)
    this.decodeCanvas = document.createElement('canvas');
    this.decodeCtx = this.decodeCanvas.getContext('2d', { willReadFrequently: true });
    this.gridCanvas = document.createElement('canvas');
    this.gridCtx = this.gridCanvas.getContext('2d', { willReadFrequently: true });

    this.querySelectorAll('[data-eg-rs-open-camera]').forEach((btn) =>
      btn.addEventListener('click', () => this.openCamera())
    );
    $('[data-eg-rs-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('close', () => this.onDialogClosed());
    this.dialog?.addEventListener('cancel', () => {}); // native Esc close is fine
    this.manualToggle?.addEventListener('click', () => this.toggleManual());
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    this.emailInput?.addEventListener('input', () => this.clearError());
    this.codeInput?.addEventListener('input', () => this.clearError());

    this._onPageHide = () => this.stopCamera();
    window.addEventListener('pagehide', this._onPageHide);
  }

  disconnectedCallback() {
    this.stopCamera();
    window.removeEventListener('pagehide', this._onPageHide);
  }

  /* ── Small helpers ─────────────────────────────────────────── */

  t(key) {
    return this.dataset[key] || '';
  }

  announce(message) {
    if (this.statusEl) this.statusEl.textContent = message || '';
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (!prefersReducedMotion()) {
      this.form.classList.remove('is-shaking');
      void this.form.offsetWidth; // restart the shake animation
      this.form.classList.add('is-shaking');
    }
  }

  clearError() {
    if (this.errorEl && !this.errorEl.hidden) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  toggleManual(forceOpen) {
    const open = forceOpen === true || this.manualWrap.hidden;
    this.manualWrap.hidden = !open;
    this.manualToggle.setAttribute('aria-expanded', String(open));
    if (open) this.codeInput?.focus();
  }

  /** Reflect a confirmed scan in the form: tile → detected chip. */
  applyDetected(orderId) {
    this.orderId = orderId;
    this.clearError();
    if (this.detectedText) {
      this.detectedText.textContent = fill(this.t('tDetectedChip'), { order: orderId });
    }
    if (this.scanTile) this.scanTile.hidden = true;
    if (this.detectedChip) {
      this.detectedChip.hidden = false;
      this.detectedChip.classList.remove('is-arriving');
      void this.detectedChip.offsetWidth;
      this.detectedChip.classList.add('is-arriving');
    }
    this.submitBtn?.focus({ preventScroll: true });
  }

  /* ── Camera / scanning ─────────────────────────────────────── */

  async openCamera() {
    this.clearError();
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      this.showError(this.t('tErrCameraUnavailable'));
      this.toggleManual(true);
      return;
    }

    this.dialog.showModal();
    this.dialog.classList.remove('is-captured');
    this.banner.hidden = true;
    this.captureCanvas.hidden = true;
    this.locked = false;
    this.lastRead = '';
    this.readStreak = 0;
    this.contour = null;
    this.contourLength = 0;
    this.accentColor = getComputedStyle(this).getPropertyValue('--eg-rs-accent').trim() || '#7c4fd9';
    this.reduceMotion = prefersReducedMotion();
    if (this.cameraHint) this.cameraHint.textContent = this.t('tCameraSearching');

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
    } catch (error) {
      this.dialog.close();
      const denied = error && (error.name === 'NotAllowedError' || error.name === 'SecurityError');
      this.showError(this.t(denied ? 'tErrCameraDenied' : 'tErrCameraUnavailable'));
      this.toggleManual(true);
      return;
    }

    this.video.srcObject = this.stream;
    try {
      await this.video.play();
    } catch {
      /* autoplay + muted + playsinline: benign on some engines */
    }

    this.announce(this.t('tSrScanning'));
    await this.initDetector();
    this.loop();
  }

  /** Pick the scan engine once per session: native detector or JS fallback. */
  async initDetector() {
    if (this.detector) return;

    if ('BarcodeDetector' in window) {
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        const wanted = ['code_128', 'code_39', 'itf', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'codabar'];
        const formats = wanted.filter((f) => supported.includes(f));
        if (formats.length) {
          const native = new window.BarcodeDetector({ formats });
          this.detector = {
            kind: 'native',
            detect: async (canvas) => {
              const hits = await native.detect(canvas);
              if (!hits || !hits.length) return null;
              const hit = hits[0];
              const box = hit.boundingBox;
              return {
                value: hit.rawValue,
                extent: box ? { x0: box.x, y0: box.y, x1: box.x + box.width, y1: box.y + box.height } : null,
              };
            },
          };
          return;
        }
      } catch {
        /* fall through to the JS decoder */
      }
    }

    const readerSrc = this.dataset.readerSrc;
    const mod = await import(readerSrc);
    this.detector = {
      kind: 'fallback',
      detect: async (canvas, ctx) => {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hit = mod.decodeCode128(imageData);
        return hit ? { value: hit.value, extent: hit.extent } : null;
      },
    };
  }

  loop() {
    cancelAnimationFrame(this.rafId);
    const step = (now) => {
      if (!this.stream || this.locked) return;
      if (this.video.readyState >= 2 && this.video.videoWidth > 0) {
        if (now - this.lastOutlineAt >= OUTLINE_INTERVAL_MS) {
          this.lastOutlineAt = now;
          this.computeContour();
        }
        if (now - this.lastDecodeAt >= DECODE_INTERVAL_MS) {
          this.lastDecodeAt = now;
          // fire-and-forget; guarded against overlap via _decoding
          this.tryDecode();
        }
        // Redrawn every frame so the border beam glides at full frame rate
        // even though the contour itself only updates every ~90ms.
        this.drawOverlay(now);
      }
      this.rafId = requestAnimationFrame(step);
    };
    this.rafId = requestAnimationFrame(step);
  }

  async tryDecode() {
    if (this._decoding || !this.detector) return;
    this._decoding = true;
    try {
      const vw = this.video.videoWidth;
      const vh = this.video.videoHeight;
      // Pass 1: the whole frame (barcode anywhere). Pass 2: the center region
      // matching the on-screen scan frame — less downscaling AND the per-line
      // threshold adapts to the receipt instead of the dark surroundings.
      let hit = await this.detectRegion(0, 0, vw, vh);
      if (!hit) hit = await this.detectRegion(vw * 0.1, vh * 0.19, vw * 0.8, vh * 0.62);
      if (!hit || !hit.value) {
        this.readStreak = 0;
        this.lastRead = '';
        return;
      }
      const digits = extractOrderId(hit.value);
      if (!digits) return;

      this.readStreak = digits === this.lastRead ? this.readStreak + 1 : 1;
      this.lastRead = digits;
      if (this.readStreak >= CONFIRM_READS) {
        this.lock(digits, hit.extent);
      } else if (this.cameraHint) {
        this.cameraHint.textContent = this.t('tCameraHoldStill');
      }
    } catch {
      /* a failed frame is retried on the next tick */
    } finally {
      this._decoding = false;
    }
  }

  /**
   * Run the active scan engine on a crop of the video frame.
   * Returns { value, extent } with the extent already mapped back to native
   * video pixel coordinates, or null.
   */
  async detectRegion(sx, sy, sw, sh) {
    const scale = Math.min(1, DECODE_MAX_WIDTH / sw);
    const w = Math.round(sw * scale);
    const h = Math.round(sh * scale);
    if (this.decodeCanvas.width !== w || this.decodeCanvas.height !== h) {
      this.decodeCanvas.width = w;
      this.decodeCanvas.height = h;
    }
    this.decodeCtx.drawImage(this.video, sx, sy, sw, sh, 0, 0, w, h);
    const hit = await this.detector.detect(this.decodeCanvas, this.decodeCtx);
    if (!hit || !hit.value) return null;
    const extent = hit.extent
      ? {
          x0: hit.extent.x0 / scale + sx,
          y0: hit.extent.y0 / scale + sy,
          x1: hit.extent.x1 / scale + sx,
          y1: hit.extent.y1 / scale + sy,
        }
      : null;
    return { value: hit.value, extent };
  }

  /* ── Receipt outline highlight (gamification layer) ────────── */

  syncOverlaySize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.video.getBoundingClientRect();
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);
    if (this.overlay.width !== w || this.overlay.height !== h) {
      this.overlay.width = w;
      this.overlay.height = h;
    }
  }

  /**
   * Find the paper's actual outline: largest bright component on a coarse
   * luminance grid, then Moore-neighbor boundary tracing around it — so the
   * highlight hugs the receipt's real (possibly skewed, curled) edges
   * instead of stretching a rectangle over it. Returns an ordered polygon
   * in native video pixels, or null.
   */
  findPaperContour() {
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    const gw = GRID_W;
    const gh = Math.max(12, Math.round((gw * vh) / vw));
    if (this.gridCanvas.width !== gw || this.gridCanvas.height !== gh) {
      this.gridCanvas.width = gw;
      this.gridCanvas.height = gh;
    }
    this.gridCtx.drawImage(this.video, 0, 0, gw, gh);
    const { data } = this.gridCtx.getImageData(0, 0, gw, gh);

    const lum = new Uint8ClampedArray(gw * gh);
    let mean = 0;
    for (let i = 0, p = 0; i < lum.length; i += 1, p += 4) {
      lum[i] = (data[p] * 77 + data[p + 1] * 151 + data[p + 2] * 28) >> 8;
      mean += lum[i];
    }
    mean /= lum.length;
    const threshold = Math.max(mean + 22, 130);

    // Label 4-connected bright components, keep the largest.
    const labels = new Int16Array(gw * gh); // 0 = unvisited/background
    let bestId = 0;
    let bestSize = 0;
    let nextId = 0;
    const stack = [];
    for (let start = 0; start < lum.length; start += 1) {
      if (labels[start] || lum[start] < threshold) continue;
      nextId += 1;
      let size = 0;
      stack.length = 0;
      stack.push(start);
      labels[start] = nextId;
      while (stack.length) {
        const idx = stack.pop();
        const x = idx % gw;
        size += 1;
        if (x > 0 && !labels[idx - 1] && lum[idx - 1] >= threshold) { labels[idx - 1] = nextId; stack.push(idx - 1); }
        if (x < gw - 1 && !labels[idx + 1] && lum[idx + 1] >= threshold) { labels[idx + 1] = nextId; stack.push(idx + 1); }
        if (idx >= gw && !labels[idx - gw] && lum[idx - gw] >= threshold) { labels[idx - gw] = nextId; stack.push(idx - gw); }
        if (idx < lum.length - gw && !labels[idx + gw] && lum[idx + gw] >= threshold) { labels[idx + gw] = nextId; stack.push(idx + gw); }
      }
      if (size > bestSize) {
        bestSize = size;
        bestId = nextId;
      }
    }

    const frac = bestSize / (gw * gh);
    if (!bestId || frac < 0.05 || frac > 0.94) return null; // nothing paper-like

    const inside = (x, y) => x >= 0 && x < gw && y >= 0 && y < gh && labels[y * gw + x] === bestId;

    // Topmost-left cell of the component = stable trace anchor.
    let sx = -1;
    let sy = -1;
    outer: for (let y = 0; y < gh; y += 1) {
      for (let x = 0; x < gw; x += 1) {
        if (labels[y * gw + x] === bestId) {
          sx = x;
          sy = y;
          break outer;
        }
      }
    }

    // Moore-neighbor tracing (clockwise), starting with the backtrack cell
    // to the anchor's left (guaranteed outside — the anchor is topmost-left).
    const NB = [
      [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
    ];
    const points = [];
    let cx = sx;
    let cy = sy;
    let backtrack = 4; // index in NB pointing at the cell we came from (W)
    const maxSteps = bestSize * 8 + 32;
    for (let step = 0; step < maxSteps; step += 1) {
      points.push({ x: cx, y: cy });
      let found = -1;
      // Scan clockwise starting just after the backtrack direction.
      for (let k = 1; k <= 8; k += 1) {
        const dir = (backtrack + k) % 8;
        const nx = cx + NB[dir][0];
        const ny = cy + NB[dir][1];
        if (inside(nx, ny)) {
          found = dir;
          cx = nx;
          cy = ny;
          break;
        }
      }
      if (found === -1) break; // isolated cell
      backtrack = (found + 4) % 8; // now pointing back where we came from
      if (cx === sx && cy === sy && points.length > 2) break; // closed the loop
    }
    if (points.length < 8) return null;

    // Grid cells → video pixels (cell centers).
    const cw = vw / gw;
    const ch = vh / gh;
    return points.map((p) => ({ x: (p.x + 0.5) * cw, y: (p.y + 0.5) * ch }));
  }

  /** Resample a closed polygon to `n` points evenly spaced by arc length. */
  resampleClosed(points, n) {
    const segs = [];
    let total = 0;
    for (let i = 0; i < points.length; i += 1) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      segs.push(len);
      total += len;
    }
    if (total <= 0) return null;
    const out = [];
    const step = total / n;
    let target = 0;
    let acc = 0;
    let i = 0;
    for (let k = 0; k < n; k += 1) {
      while (acc + segs[i] < target) {
        acc += segs[i];
        i = (i + 1) % points.length;
      }
      const a = points[i];
      const b = points[(i + 1) % points.length];
      const t = segs[i] > 0 ? (target - acc) / segs[i] : 0;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      target += step;
    }
    return { points: out, length: total };
  }

  /** Update this.contour (overlay coords, CONTOUR_POINTS entries, lerped). */
  computeContour() {
    this.syncOverlaySize();
    const raw = this.findPaperContour();
    if (!raw) {
      this.contour = null;
      return;
    }
    // Video px → overlay px (object-fit: cover mapping).
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    const dw = this.overlay.width;
    const dh = this.overlay.height;
    const scale = Math.max(dw / vw, dh / vh);
    const ox = (dw - vw * scale) / 2;
    const oy = (dh - vh * scale) / 2;
    const mapped = raw.map((p) => ({ x: p.x * scale + ox, y: p.y * scale + oy }));
    const resampled = this.resampleClosed(mapped, CONTOUR_POINTS);
    if (!resampled) {
      this.contour = null;
      return;
    }
    if (this.contour && this.contour.length === CONTOUR_POINTS) {
      const k = 0.4; // low-pass so the outline glides instead of jittering
      for (let i = 0; i < CONTOUR_POINTS; i += 1) {
        this.contour[i].x += (resampled.points[i].x - this.contour[i].x) * k;
        this.contour[i].y += (resampled.points[i].y - this.contour[i].y) * k;
      }
    } else {
      this.contour = resampled.points;
    }
    this.contourLength = resampled.length;
  }

  /** Smooth closed path through the contour (quadratic midpoint curve). */
  buildContourPath() {
    const pts = this.contour;
    const path = new Path2D();
    const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    let m = mid(pts[0], pts[1]);
    path.moveTo(m.x, m.y);
    for (let i = 1; i <= pts.length; i += 1) {
      const p = pts[i % pts.length];
      const next = pts[(i + 1) % pts.length];
      m = mid(p, next);
      path.quadraticCurveTo(p.x, p.y, m.x, m.y);
    }
    path.closePath();
    return path;
  }

  /**
   * Per-frame overlay render: the receipt contour with a soft glow, plus a
   * "border beam" — a bright light segment racing along the traced edge
   * (ported to canvas from the border-beam CSS effect; skipped under
   * prefers-reduced-motion).
   */
  drawOverlay(now) {
    const ctx = this.overlay.getContext('2d');
    ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
    if (!this.contour || this.contour.length < 8) return;

    const accent = this.accentColor;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const path = this.buildContourPath();

    ctx.save();
    ctx.lineJoin = 'round';
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2.5 * dpr;
    ctx.shadowColor = accent;
    ctx.shadowBlur = 14 * dpr;
    // Gentle breathing on the base outline (kept static under reduced motion).
    ctx.globalAlpha = this.reduceMotion ? 0.9 : 0.65 + 0.3 * Math.sin((now % 2000) / 2000 * Math.PI * 2);
    ctx.stroke(path);
    ctx.restore();

    if (this.reduceMotion || !this.contourLength) return;

    const t = (now % BEAM_LAP_MS) / BEAM_LAP_MS;
    const total = this.contourLength;
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.setLineDash([total * 0.16, total * 0.84]);
    ctx.lineDashOffset = -t * total;
    // Wide accent halo…
    ctx.strokeStyle = accent;
    ctx.lineWidth = 5 * dpr;
    ctx.shadowColor = accent;
    ctx.shadowBlur = 24 * dpr;
    ctx.globalAlpha = 0.85;
    ctx.stroke(path);
    // …with a hot white core.
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2 * dpr;
    ctx.shadowBlur = 10 * dpr;
    ctx.globalAlpha = 0.95;
    ctx.stroke(path);
    ctx.restore();
  }

  drawBox(ctx, box, emphasized) {
    const accent = getComputedStyle(this).getPropertyValue('--eg-rs-accent').trim() || '#7c4fd9';
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = 14 * dpr;
    ctx.save();
    ctx.strokeStyle = accent;
    ctx.lineWidth = (emphasized ? 4 : 2.5) * dpr;
    ctx.shadowColor = accent;
    ctx.shadowBlur = (emphasized ? 26 : 14) * dpr;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(box.x, box.y, box.w, box.h, r);
    } else {
      ctx.rect(box.x, box.y, box.w, box.h);
    }
    ctx.stroke();
    ctx.restore();
  }

  /* ── Detection lock: capture → celebrate → back to the form ── */

  /** @param extent barcode bounds in native video pixel coordinates */
  lock(orderId, extent) {
    if (this.locked) return;
    this.locked = true;
    cancelAnimationFrame(this.rafId);

    if (navigator.vibrate) navigator.vibrate(35);

    // Freeze the moment: photo of the current frame…
    const rect = this.video.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.captureCanvas.width = Math.round(rect.width * dpr);
    this.captureCanvas.height = Math.round(rect.height * dpr);
    const cctx = this.captureCanvas.getContext('2d');
    // Recreate object-fit: cover cropping on the capture canvas
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    const scale = Math.max(this.captureCanvas.width / vw, this.captureCanvas.height / vh);
    const ox = (this.captureCanvas.width - vw * scale) / 2;
    const oy = (this.captureCanvas.height - vh * scale) / 2;
    cctx.drawImage(this.video, ox, oy, vw * scale, vh * scale);

    // …with the barcode outline burned in for the "locked on" feel.
    if (extent) {
      const pad = 8 * dpr; // breathing room so the box frames the code
      const mapped = {
        x: extent.x0 * scale + ox - pad,
        y: extent.y0 * scale + oy - pad,
        w: (extent.x1 - extent.x0) * scale + pad * 2,
        h: (extent.y1 - extent.y0) * scale + pad * 2,
      };
      this.drawBox(cctx, mapped, true);
    }

    this.captureCanvas.hidden = false;
    if (this.flashEl) {
      this.flashEl.classList.remove('is-flashing');
      void this.flashEl.offsetWidth;
      this.flashEl.classList.add('is-flashing');
    }
    // The last live contour frame is stale now — clear the overlay; it hosts
    // the card's border beam once the photo settles.
    const octx = this.overlay.getContext('2d');
    octx.clearRect(0, 0, this.overlay.width, this.overlay.height);
    // Next frame: photo animates from full-bleed to the centered card.
    requestAnimationFrame(() => this.dialog.classList.add('is-captured'));
    // Border beam appears once the card's 620ms fly-to-center settles.
    this.captureBeamTimer = setTimeout(() => this.startCaptureBeam(), 700);

    const title = fill(this.t('tDetectedTitle'), { order: orderId });
    if (this.bannerText) this.bannerText.textContent = title;
    if (this.banner) this.banner.hidden = false;
    if (this.cameraHint) this.cameraHint.textContent = this.t('tDetectedNote');
    this.announce(title);

    // Camera can stop immediately — the captured canvas carries the visual.
    this.stopStream();

    this.closeTimer = setTimeout(() => {
      this.dialog.close();
      this.applyDetected(orderId);
    }, CAPTURE_LINGER_MS);
  }

  /**
   * Border beam around the settled photo card, drawn on the overlay canvas
   * (same dash-offset technique as the live contour beam — CSS mask rings
   * proved unreliable across engines).
   */
  startCaptureBeam() {
    if (this.reduceMotion || !this.dialog.open) return;
    const cardRect = this.captureCanvas.getBoundingClientRect();
    const overlayRect = this.overlay.getBoundingClientRect();
    if (!cardRect.width || !overlayRect.width) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const inset = 1.5 * dpr; // ride on the card's border ring
    const x = (cardRect.left - overlayRect.left) * dpr + inset;
    const y = (cardRect.top - overlayRect.top) * dpr + inset;
    const w = cardRect.width * dpr - inset * 2;
    const h = cardRect.height * dpr - inset * 2;
    // border-radius is pre-transform CSS px — scale it like the card was.
    const ratio = this.captureCanvas.offsetWidth ? cardRect.width / this.captureCanvas.offsetWidth : 1;
    const r = Math.max(6 * dpr, 26 * ratio * dpr - inset);
    const path = new Path2D();
    if (typeof path.roundRect === 'function') path.roundRect(x, y, w, h, r);
    else path.rect(x, y, w, h);
    const perimeter = 2 * (w + h);
    const ctx = this.overlay.getContext('2d');
    const accent = this.accentColor;

    const step = (now) => {
      if (!this.dialog.open || !this.dialog.classList.contains('is-captured')) return;
      ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
      const t = (now % 2600) / 2600;
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.setLineDash([perimeter * 0.2, perimeter * 0.8]);
      ctx.lineDashOffset = -t * perimeter;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 5 * dpr;
      ctx.shadowColor = accent;
      ctx.shadowBlur = 26 * dpr;
      ctx.globalAlpha = 0.9;
      ctx.stroke(path);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 * dpr;
      ctx.shadowBlur = 12 * dpr;
      ctx.stroke(path);
      ctx.restore();
      this.captureBeamRafId = requestAnimationFrame(step);
    };
    cancelAnimationFrame(this.captureBeamRafId);
    this.captureBeamRafId = requestAnimationFrame(step);
  }

  onDialogClosed() {
    clearTimeout(this.closeTimer);
    this.closeTimer = 0;
    clearTimeout(this.captureBeamTimer);
    this.captureBeamTimer = 0;
    cancelAnimationFrame(this.captureBeamRafId);
    this.captureBeamRafId = 0;
    this.stopCamera();
    this.dialog.classList.remove('is-captured');
    this.banner.hidden = true;
    this.captureCanvas.hidden = true;
    this.contour = null;
    this.contourLength = 0;
    const ctx = this.overlay.getContext('2d');
    ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
  }

  stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.video) this.video.srcObject = null;
  }

  stopCamera() {
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.locked = false;
    this.stopStream();
  }

  /* ── Claim submission ──────────────────────────────────────── */

  getOrderId() {
    if (this.orderId) return this.orderId;
    // Manual entry accepts the same shapes the barcode can carry
    // ("R:13526292169086", plain digits, stray spaces).
    const value = (this.codeInput?.value || '').trim();
    if (!value) return null;
    return extractOrderId(value) || value; // invalid input falls through to the format error
  }

  setSubmitting(busy) {
    this.submitting = busy;
    if (this.submitBtn) {
      this.submitBtn.disabled = busy;
      this.submitBtn.classList.toggle('is-loading', busy);
    }
    if (this.submitLabel) {
      if (busy) {
        this._idleLabel = this.submitLabel.textContent;
        this.submitLabel.textContent = this.t('tSubmitting');
      } else if (this._idleLabel) {
        this.submitLabel.textContent = this._idleLabel;
      }
    }
  }

  buildEndpoint() {
    const base = (this.dataset.proxyBase || '').trim().replace(/\/+$/, '');
    // Refuse anything that is not a same-origin relative path — an absolute
    // URL would skip app-proxy signing and leak the email cross-origin.
    // Backslash is rejected too: URL parsing treats "/\host" as "//host".
    if (!/^\/(?![/\\])/.test(base)) return '';
    return `${base}/pointTransactions/pos`;
  }

  async submit() {
    if (this.submitting) return;
    this.clearError();

    const email = (this.emailInput?.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      this.showError(this.t('tErrInvalidEmail'));
      this.emailInput?.focus();
      return;
    }

    const orderId = this.getOrderId();
    if (!orderId) {
      this.showError(this.t('tErrMissingOrder'));
      return;
    }
    if (!ORDER_ID_RE.test(orderId)) {
      this.showError(this.t('tErrInvalidOrder'));
      this.codeInput?.focus();
      return;
    }

    const endpoint = this.buildEndpoint();
    if (!endpoint) {
      this.showError(this.t('tErrGeneric'));
      return;
    }

    this.setSubmitting(true);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ orderId, email }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data && data.error) {
          // Backend detail for debugging; visitors see the localized copy.
          console.warn('[eg-receipt-scanner]', response.status, data.error);
        }
        this.showError(this.messageForStatus(response.status));
        return;
      }

      this.celebrate(Number(data.points) || 0, email);
    } catch {
      this.showError(this.t('tErrNetwork'));
    } finally {
      this.setSubmitting(false);
    }
  }

  messageForStatus(status) {
    if (status === 409) return this.t('tErrAlreadyClaimed');
    if (status === 422) return this.t('tErrNotPaid');
    if (status === 404) return this.t('tErrNotFound');
    return this.t('tErrGeneric');
  }

  /* ── Success celebration ───────────────────────────────────── */

  celebrate(points, email) {
    if (this.successBody) {
      this.successBody.textContent = fill(this.successBody.dataset.tSuccessBody, { email });
    }
    this.form.hidden = true;
    this.successPanel.hidden = false;
    this.successPanel.classList.add('is-entering');

    const pointsText = (n) => fill(this.t('tSuccessPoints'), { points: n });
    if (this.pointsEl) {
      if (points > 0) {
        this.pointsEl.hidden = false;
        if (prefersReducedMotion()) {
          this.pointsEl.textContent = pointsText(points);
        } else {
          const startAt = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - startAt) / COUNT_UP_MS);
            const eased = 1 - Math.pow(1 - p, 3);
            this.pointsEl.textContent = pointsText(Math.round(points * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          // rAF can be throttled to a standstill (backgrounded/occluded tab)
          // — guarantee the final value regardless.
          setTimeout(() => {
            this.pointsEl.textContent = pointsText(points);
          }, COUNT_UP_MS + 120);
        }
      } else {
        this.pointsEl.hidden = true;
      }
    }

    if (this.burstEl && !prefersReducedMotion()) {
      this.burstEl.textContent = '';
      for (let i = 0; i < 18; i += 1) {
        const spark = document.createElement('span');
        spark.className = 'eg-rs__spark';
        const angle = Math.random() * Math.PI * 2;
        const dist = 70 + Math.random() * 120;
        spark.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        spark.style.setProperty('--dy', `${Math.sin(angle) * dist - 40}px`);
        spark.style.setProperty('--delay', `${Math.random() * 220}ms`);
        spark.style.setProperty('--size', `${5 + Math.random() * 7}px`);
        this.burstEl.appendChild(spark);
      }
    }

    const announcement = `${this.successTitle?.textContent || ''} ${points > 0 ? pointsText(points) : ''}`.trim();
    this.announce(announcement);
    this.successTitle?.focus({ preventScroll: true });
    this.successPanel.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

if (!window.customElements.get('eg-receipt-scanner')) {
  window.customElements.define('eg-receipt-scanner', EGReceiptScanner);
}
